import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, ChevronDown, ChevronRight,
  User, UserCircle, Settings, LogOut, Globe, Shield, FileText, Upload,
  Home, Package, AlertTriangle, HelpCircle, UserPlus, CheckCircle
} from "lucide-react";
import { useTheme } from "../ThemeContext.tsx";
import ThemeToggle from "../common/ThemeToggle";
import { useAuth } from "../../context/AuthContext";
import { FaUserCircle } from "react-icons/fa";
import { getUserAvatar, getUserInitials } from "../../utils/userAvatar";
import "../../styles/site-header.css";

const AxonMetricHeader = ({ header, single }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);
  const dropdownRefs = useRef({});
  const userMenuRef = useRef(null);

  // Check if we're on the homepage
  const isHomePage = location.pathname === "/";

  // Get user authentication state
  const { user, logout } = useAuth();

  // User avatar state
  const [userAvatar, setUserAvatar] = useState(null);

  // Load avatar from user, localStorage, or AuthContext
  useEffect(() => {
    if (!user) {
      setUserAvatar(null);
      return;
    }

    const loadAvatar = () => {
      // Try multiple sources: user object, localStorage, and AuthContext
      // Include user identifier (username/id) so avatar is user-specific
      const avatarSources = {
        avatar: user?.avatar,
        profile_picture: user?.profile_picture || user?.avatar,
        first_name: user?.fullName?.split(' ')[0],
        last_name: user?.fullName?.split(' ').slice(1).join(' '),
        username: user?.username,
        id: user?.id,
        user_id: user?.user_id
      };

      const avatar = getUserAvatar(avatarSources);
      setUserAvatar(avatar);
    };

    loadAvatar();

    // Listen for avatar updates from storage and custom events
    const handleStorageChange = () => {
      loadAvatar();
    };

    const handleAvatarUpdate = () => {
      loadAvatar();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('avatarUpdated', handleAvatarUpdate);
    // Also check periodically for updates within the same tab
    const interval = setInterval(loadAvatar, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('avatarUpdated', handleAvatarUpdate);
      clearInterval(interval);
    };
  }, [user]);

  // Get user initials for fallback
  const getInitials = () => {
    if (!user) return 'U';
    const fullName = user.fullName || user.username || '';
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return fullName[0]?.toUpperCase() || 'U';
  };


  // Navigation data
  const navigationItems = [
    {
      name: "Home",
      href: "/",
      hasDropdown: false
    },
    {
      name: "About",
      href: "/about",
      hasDropdown: false
    },
    {
      name: "Services",
      href: "/service",
      hasDropdown: true,
      dropdownItems: [
        { name: "Asset Performance Monitoring", href: "/service/asset-performance", icon: Shield, description: "Real-time monitoring and analytics" },
        { name: "AI-Driven Analytics", href: "/service/ai-analytics", icon: Globe, description: "Advanced AI insights and predictions" },
        // { name: "Drone Thermography", href: "/service/drone-thermography", icon: Settings, description: "Aerial thermal imaging services" }, // Commented out for future use
        { name: "Portfolio Management", href: "/service/portfolio-management", icon: User, description: "Comprehensive portfolio solutions" }
      ]
    },
    {
      name: "News",
      href: "/news",
      hasDropdown: false
    },
    {
      name: "Contact",
      href: "/contact",
      hasDropdown: false
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    const handleClickOutside = (event) => {
      // Check if click is outside mobile menu
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }

      // Check if click is outside user menu
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }


      // Close navigation dropdowns when clicking outside
      setActiveDropdown(null);
      setHoveredDropdown(null);
      setDropdownOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
        setHoveredDropdown(null);
        setIsUserMenuOpen(false);
        setDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Additional effect to handle clicks when user menu is open
  useEffect(() => {
    if (isUserMenuOpen) {
      const handleDocumentClick = (event) => {
        // Simple check: if click is not on user menu button or dropdown, close it
        const isUserMenuButton = userMenuRef.current && userMenuRef.current.contains(event.target);
        const isDropdown = event.target.closest('[data-user-menu-dropdown]');

        if (!isUserMenuButton && !isDropdown) {
          setIsUserMenuOpen(false);
        }
      };

      // Use a small delay to prevent immediate closure when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleDocumentClick);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleDocumentClick);
      };
    }
  }, [isUserMenuOpen]);

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const handleDropdownHover = (index) => {
    setHoveredDropdown(index);
  };

  const handleDropdownLeave = () => {
    setHoveredDropdown(null);
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setActiveDropdown(null);
    setHoveredDropdown(null);
  };


  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    navigate('/profile');
  };

  const { isDarkMode, toggleTheme } = useTheme();

  // Function to check if a navigation item is active
  const isActive = (href) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <header
        id="header-sticky"
        className={`header-3 nav-professional fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isDarkMode
            ? isScrolled
              ? 'bg-[#0B1120]/95 backdrop-blur-xl shadow-2xl border-b border-slate-800'
              : 'bg-[#0B1120]/85 backdrop-blur-lg shadow-lg border-b border-slate-800/80'
            : isScrolled
              ? 'bg-white/95 backdrop-blur-xl shadow-2xl border-b border-gray-200'
              : 'bg-white/80 backdrop-blur-lg shadow-lg border-b border-gray-200'
        }`}
        style={{
          height: '50px',
          backgroundColor: isDarkMode
            ? (isScrolled ? 'rgba(11, 17, 32, 0.95)' : 'rgba(11, 17, 32, 0.85)')
            : (isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)'),
          backdropFilter: 'blur(16px)',
        }}
      >
        <div className="w-full h-full">
          <div className="mega-menu-wrapper h-full">
            <div className="header-main h-full flex items-center justify-between px-4 lg:px-6 relative">
              {/* Logo on the left */}
              <motion.div
                className="sticky-logo"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Link to="/" className="flex items-center group">
                  <div className="relative">
                    <img src="/Axon-logo.png" alt="AxonMetric Logo" style={{ height: '32px' }} className="transition-transform duration-300 group-hover:scale-105" />
                    <div className={`absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isDarkMode
                      ? 'bg-[#0084CF]/20'
                      : 'bg-[#0084CF]/10'
                      }`}></div>
                  </div>
                </Link>
              </motion.div>

              {/* Right side container for navigation and user actions */}
              <div className="flex items-center space-x-4">
                {/* Desktop Navigation - moved to right side */}
                <nav className="hidden lg:flex items-center space-x-4 list-none">
                  {navigationItems.map((item, index) => (
                    <motion.div
                      key={item.name}
                      className="py-4 relative group"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                      onMouseEnter={() => item.hasDropdown && handleDropdownHover(index)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <Link
                        to={item.hasDropdown ? "#" : item.href}
                        onClick={(e) => {
                          if (item.hasDropdown) {
                            e.preventDefault();
                            setActiveDropdown(activeDropdown === index ? null : index);
                          }
                        }}
                        className={`axm-nav-link ${isActive(item.href) ? 'is-active' : ''} transition-colors duration-200 flex items-center relative
                        ${isActive(item.href)
                            ? isDarkMode
                              ? 'text-[#8AC4E6]'
                              : 'text-[#1C4D88]'
                            : isDarkMode
                              ? 'text-slate-300 hover:text-[#8AC4E6]'
                              : 'text-[#3B4754] hover:text-[#0066A3]'
                          }
                      `}
                        style={{ fontWeight: isActive(item.href) ? 600 : 500 }}
                      >
                        {item.name}
                        {item.hasDropdown && (
                          <ChevronDown className={`ml-1 w-4 h-4 transition-transform duration-200 group-hover:rotate-180 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'
                            }`} />
                        )}
                        <span className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-300 ${isActive(item.href)
                          ? 'w-full'
                          : 'w-0 group-hover:w-full'
                          } ${isDarkMode
                            ? 'bg-[#8AC4E6]'
                            : 'bg-[#0084CF]'
                          }`}></span>
                      </Link>

                      {/* Enhanced Desktop Dropdown */}
                      {item.hasDropdown && (
                        <AnimatePresence>
                          {(hoveredDropdown === index || activeDropdown === index) && (
                            <motion.div
                              className={`absolute top-full min-w-64 py-2 z-50 rounded-xl border ${
                                // Dynamic positioning based on item position
                                index >= navigationItems.length - 2
                                  ? 'right-0' // Right-align for last two items (News, Contact)
                                  : 'left-0' // Left-align for other items
                                } ${isDarkMode
                                  ? 'bg-slate-800/95 backdrop-blur-xl shadow-2xl border-slate-600/50'
                                  : 'bg-white/95 backdrop-blur-xl shadow-2xl border-gray-200/50'
                                }`}
                              initial={{ opacity: 0, y: 10, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: 10, scale: 0.95 }}
                              transition={{ duration: 0.2, ease: "easeOut" }}
                              style={{
                                marginTop: '8px',
                                boxShadow: isDarkMode
                                  ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
                                  : '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.8)'
                              }}
                            >
                              {/* Dropdown Arrow */}
                              <div className={`absolute -top-2 w-4 h-4 rotate-45 ${
                                // Position arrow based on dropdown alignment
                                index >= navigationItems.length - 2
                                  ? 'right-4' // Right-align arrow for last two items
                                  : 'left-4' // Left-align arrow for other items
                                } ${isDarkMode
                                  ? 'bg-slate-800 border-l border-t border-slate-600'
                                  : 'bg-white border-l border-t border-gray-200'
                                }`}></div>

                              <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-slate-600/50' : 'border-gray-200/50'
                                }`}>
                                <h3 className={`axm-nav-panel-title ${isDarkMode ? 'text-slate-300' : 'text-[#6B7A8B]'
                                  }`}>{item.name}</h3>
                                <p className={`axm-nav-panel-sub mt-1 ${isDarkMode ? 'text-slate-400' : 'text-[#52606F]'
                                  }`}>Explore {item.name.toLowerCase()}</p>
                              </div>

                              <ul className="py-1 list-none">
                                {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                                  <li key={dropdownIndex} className="list-none">
                                    <Link
                                      to={dropdownItem.href}
                                      onClick={() => {
                                        setActiveDropdown(null);
                                        setHoveredDropdown(null);
                                      }}
                                      className={`flex items-center px-4 py-2.5 transition-colors duration-200 group/item rounded-md mx-1 text-sm ${isDarkMode
                                        ? 'text-slate-200 hover:text-[#8AC4E6] hover:bg-slate-700/50'
                                        : 'hover:bg-[#F2F8FC]'
                                        }`}
                                      style={{ fontWeight: 500, color: isDarkMode ? undefined : '#16202B' }}
                                    >
                                      <div className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center mr-3 transition-all duration-200 ${isDarkMode
                                        ? 'bg-slate-700 group-hover/item:bg-[#0084CF]/20'
                                        : 'bg-gray-100 group-hover/item:bg-[#E3F0F9]'
                                        }`}>
                                        <dropdownItem.icon className={`w-4 h-4 transition-colors duration-200 ${isDarkMode
                                          ? 'text-slate-400 group-hover/item:text-[#8AC4E6]'
                                          : 'text-gray-500 group-hover/item:text-[#0066A3]'
                                          }`} />
                                      </div>
                                      <div className="flex-1">
                                        <span
                                          className={`axm-nav-item-name block transition-colors duration-200 ${
                                            isDarkMode
                                              ? 'text-slate-100 group-hover/item:text-[#3EA3DA]'
                                              : 'text-[#16202B] group-hover/item:text-[#0066A3]'
                                          }`}
                                          style={{ fontWeight: 500 }}
                                        >{dropdownItem.name}</span>
                                        <span className={`axm-nav-item-desc block mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-[#6B7A8B]'
                                          }`}>{dropdownItem.description}</span>
                                      </div>
                                      <ChevronRight className={`w-4 h-4 transition-all duration-200 ${isDarkMode
                                        ? 'text-slate-500 group-hover/item:text-[#8AC4E6]'
                                        : 'text-gray-300 group-hover/item:text-[#0066A3]'
                                        }`} />
                                    </Link>
                                  </li>
                                ))}
                              </ul>

                              {/* Dropdown Footer */}
                              <div className={`px-4 py-2 border-t rounded-b-xl ${isDarkMode
                                ? 'border-slate-600/50 bg-slate-700/30'
                                : 'border-gray-200/50 bg-gray-50/50'
                                }`}>
                                <Link
                                  to={item.href}
                                  className={`axm-nav-viewall transition-colors duration-200 flex items-center ${isDarkMode
                                    ? 'text-[#3EA3DA] hover:text-[#8AC4E6]'
                                    : 'text-[#0084CF] hover:text-[#0066A3]'
                                    }`}
                                >
                                  View all {item.name.toLowerCase()}
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Link>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </motion.div>
                  ))}
                </nav>

                {/* Right side actions */}
                <div className="flex items-center space-x-3">

                  {/* User Menu or Login Button */}
                  {location.pathname === '/login' || location.pathname.startsWith('/login/') ? (
                    // Login page specific actions - Dark/Light mode toggle
                    <motion.div
                      className="flex items-center"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      <ThemeToggle variant="icon" size="md" />
                    </motion.div>
                  ) : user ? (
                    <div className="flex items-center gap-1.5">
                      <ThemeToggle variant="icon" size="md" />
                    <motion.div
                      className="relative flex items-center space-x-2"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      ref={userMenuRef}
                      style={{ zIndex: 60 }}
                    >
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUserMenuToggle();
                        }}
                        className={`flex items-center gap-2 px-2 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${isDarkMode
                          ? 'text-slate-200 hover:bg-slate-700/50'
                          : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        title={user.fullName || user.username}
                      >
                        {/* User Avatar or Icon - Always Visible */}
                        <div className="relative w-8 h-8">
                          {userAvatar ? (
                            <img
                              src={userAvatar}
                              alt={user.fullName || user.username}
                              className="w-full h-full rounded-full object-cover"
                              onError={(e) => {
                                // Fallback to initials if image fails
                                e.currentTarget.style.display = 'none';
                                const parent = e.currentTarget.parentElement;
                                if (parent) {
                                  const fallback = document.createElement('div');
                                  fallback.className = 'w-full h-full bg-[#0066A3] flex items-center justify-center text-white text-xs font-bold rounded-full';
                                  fallback.textContent = getInitials();
                                  parent.appendChild(fallback);
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-[#0066A3] flex items-center justify-center text-white text-xs font-bold rounded-full">
                              {getInitials()}
                            </div>
                          )}
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
                        </div>
                        {/* User Info - Hidden on mobile, visible on larger screens */}
                        <div className="text-left hidden md:block">
                          <span className="axonmetric-user-menu-name text-sm block text-[#16202B]" style={{ fontWeight: 600 }}>{user.username}</span>
                          <div className={`axonmetric-user-menu-subtitle text-xs ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`} style={{ fontWeight: 500 }}>
                            {user.fullName || user.username}
                          </div>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''} hidden md:block ${isDarkMode ? 'text-slate-400' : 'text-gray-400'
                          }`} />
                      </motion.button>

                      <AnimatePresence>
                        {isUserMenuOpen && (
                          <motion.div
                            data-user-menu-dropdown
                            className={`absolute right-0 top-full mt-2 w-64 rounded-xl shadow-2xl z-[60] border ${isDarkMode
                              ? 'bg-slate-800/95 backdrop-blur-xl border-slate-600/50'
                              : 'bg-white/95 backdrop-blur-xl border-gray-200/50'
                              }`}
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            style={{ position: 'absolute', top: '100%', right: 0 }}
                          >
                            {/* Dropdown Arrow */}
                            <div className={`absolute -top-2 right-4 w-4 h-4 rotate-45 ${isDarkMode
                              ? 'bg-slate-800 border-l border-t border-slate-600'
                              : 'bg-white border-l border-t border-gray-200'
                              }`}></div>

                            {/* User Info Header */}
                            <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-600/50' : 'border-gray-200/50'
                              }`}>
                              <div className="flex items-center gap-3">
                                <div className="relative w-10 h-10 flex-shrink-0">
                                  {userAvatar ? (
                                    <img
                                      src={userAvatar}
                                      alt={user.fullName || user.username}
                                      className="w-full h-full rounded-full object-cover"
                                      onError={(e) => {
                                        // Fallback to initials if image fails
                                        e.currentTarget.style.display = 'none';
                                        const parent = e.currentTarget.parentElement;
                                        if (parent) {
                                          const fallback = document.createElement('div');
                                          fallback.className = 'w-full h-full bg-[#0066A3] flex items-center justify-center text-white text-xs font-bold rounded-full';
                                          fallback.textContent = getInitials();
                                          parent.appendChild(fallback);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-[#0066A3] flex items-center justify-center text-white text-xs font-bold rounded-full">
                                      {getInitials()}
                                    </div>
                                  )}
                                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
                                </div>
                                <div>
                                  <p className={`axonmetric-user-menu-name text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'
                                    }`} style={{ fontWeight: 600 }}>
                                    {user.fullName || user.username}
                                  </p>
                                  <p className={`axonmetric-user-menu-subtitle text-xs ${isDarkMode ? 'text-slate-300' : 'text-gray-700'
                                    }`} style={{ fontWeight: 500 }}>
                                    {user.username} • {user.role || 'User'}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="px-2 py-2">
                              <div className={`axonmetric-user-menu-heading px-2 py-1 text-[11px] uppercase tracking-[0.1em] ${isDarkMode ? 'text-slate-300' : 'text-gray-600'
                                }`} style={{ fontWeight: 600 }}>
                                Quick Actions
                              </div>

                              <button
                                onClick={() => {
                                  setIsUserMenuOpen(false);
                                  navigate(`/navigate/${user.username}`);
                                }}
                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isDarkMode
                                    ? 'text-slate-100 hover:bg-slate-700/50 hover:text-[#8AC4E6]'
                                    : 'text-gray-800 hover:bg-gray-100 hover:text-[#0066A3]'
                                  }`}
                              >
                                <Globe className="w-4 h-4 mr-3" />
                                <span className="axonmetric-user-menu-action" style={{ fontWeight: 500 }}>Site Navigation</span>
                              </button>

                              <button
                                onClick={() => {
                                  setIsUserMenuOpen(false);
                                  navigate(`/alerts/${user.username}`);
                                }}
                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isDarkMode
                                    ? 'text-slate-100 hover:bg-slate-700/50 hover:text-[#8AC4E6]'
                                    : 'text-gray-800 hover:bg-gray-100 hover:text-[#0066A3]'
                                  }`}
                              >
                                <AlertTriangle className="w-4 h-4 mr-3" />
                                <span className="axonmetric-user-menu-action" style={{ fontWeight: 500 }}>Alerts & Notifications</span>
                              </button>
                            </div>

                            {/* Divider */}
                            <div className={`mx-2 h-px ${isDarkMode ? 'bg-slate-600/50' : 'bg-gray-200/50'
                              }`}></div>

                            {/* Account & Settings */}
                            <div className="px-2 py-2">
                              <div className={`axonmetric-user-menu-heading px-2 py-1 text-[11px] uppercase tracking-[0.1em] ${isDarkMode ? 'text-slate-300' : 'text-gray-600'
                                }`} style={{ fontWeight: 600 }}>
                                Account & Settings
                              </div>

                              <button
                                onClick={handleProfileClick}
                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isDarkMode
                                    ? 'text-slate-100 hover:bg-slate-700/50 hover:text-[#8AC4E6]'
                                    : 'text-gray-800 hover:bg-gray-100 hover:text-[#0066A3]'
                                  }`}
                              >
                                <Settings className="w-4 h-4 mr-3" />
                                <span className="axonmetric-user-menu-action" style={{ fontWeight: 500 }}>Profile Settings</span>
                              </button>

                              <button
                                onClick={() => {
                                  setIsUserMenuOpen(false);
                                  // Add help functionality
                                }}
                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isDarkMode
                                    ? 'text-slate-100 hover:bg-slate-700/50 hover:text-[#8AC4E6]'
                                    : 'text-gray-800 hover:bg-gray-100 hover:text-[#0066A3]'
                                  }`}
                              >
                                <HelpCircle className="w-4 h-4 mr-3" />
                                <span className="axonmetric-user-menu-action" style={{ fontWeight: 500 }}>Help & Support</span>
                              </button>

                              <div className="pt-1">
                                <ThemeToggle variant="menu-item" />
                              </div>
                            </div>

                            {/* Divider */}
                            <div className={`mx-2 h-px ${isDarkMode ? 'bg-slate-600/50' : 'bg-gray-200/50'
                              }`}></div>

                            {/* Logout */}
                            <div className="px-2 py-2">
                              <button
                                onClick={handleLogout}
                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isDarkMode
                                    ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                                    : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                                  }`}
                              >
                                <LogOut className="w-4 h-4 mr-3" />
                                <span className="axonmetric-user-menu-action" style={{ fontWeight: 500 }}>Sign Out</span>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                    </motion.div>
                    </div>
                  ) : (
                    <motion.div
                      className="flex items-center"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      <ThemeToggle variant="icon" size="md" />
                      <Link
                        to="/login"
                        className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 ${isDarkMode
                          ? 'text-slate-300 hover:text-[#8AC4E6] hover:bg-slate-700/50'
                          : 'text-gray-600 hover:text-[#0066A3] hover:bg-gray-100'
                          }`}
                      >
                        <User className="w-5 h-5" />
                      </Link>
                    </motion.div>
                  )}

                  {/* Mobile menu button */}
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className={`lg:hidden p-2.5 rounded-xl transition-all duration-300 ${isDarkMode
                      ? 'text-slate-200 hover:text-[#8AC4E6] hover:bg-slate-700/50'
                      : 'text-gray-600 hover:text-[#0066A3] hover:bg-gray-100'
                      }`}
                  >
                    {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Enhanced Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              ref={mobileMenuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, type: "spring", damping: 25, stiffness: 200 }}
              className={`fixed top-0 right-0 h-full w-80 shadow-2xl z-50 lg:hidden overflow-y-auto backdrop-blur-xl ${isDarkMode
                ? 'bg-slate-900/95 border-l border-slate-700/50'
                : 'bg-white/95 border-l border-gray-200/50'
                }`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <div className="relative">
                    <img src="/Axon-logo.png" alt="AxonMetric Logo" style={{ height: '28px' }} />
                    <div className={`absolute inset-0 rounded-lg opacity-0 hover:opacity-100 transition-opacity duration-300 ${isDarkMode
                      ? 'bg-[#0084CF]/20'
                      : 'bg-[#0084CF]/10'
                      }`}></div>
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`p-2 rounded-lg transition-all duration-300 ${isDarkMode
                      ? 'text-slate-200 hover:text-[#8AC4E6] hover:bg-slate-700/50'
                      : 'text-gray-600 hover:text-[#0066A3] hover:bg-gray-100'
                      }`}
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <nav className="space-y-2">
                  {navigationItems.map((item, index) => (
                    <div key={item.name}>
                      {item.hasDropdown ? (
                        <button
                          onClick={() => handleDropdownToggle(index)}
                          className={`axm-nav-link ${isActive(item.href) ? 'is-active' : ''} w-full flex items-center justify-between py-3 px-4 rounded-lg transition-colors duration-200 ${isActive(item.href)
                            ? isDarkMode
                              ? 'text-[#8AC4E6] bg-slate-700/50'
                              : 'text-[#1C4D88] bg-[#F2F8FC]'
                            : isDarkMode
                              ? 'text-slate-200 hover:text-[#8AC4E6] hover:bg-slate-700/50'
                              : 'text-[#3B4754] hover:text-[#0066A3] hover:bg-[#F2F8FC]'
                            }`}
                        >
                          <span>{item.name}</span>
                          <ChevronDown
                            className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''
                              }`}
                          />
                        </button>
                      ) : (
                        <Link
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`axm-nav-link ${isActive(item.href) ? 'is-active' : ''} block py-3 px-4 rounded-lg transition-colors duration-200 ${isActive(item.href)
                            ? isDarkMode
                              ? 'text-[#8AC4E6] bg-slate-700/50'
                              : 'text-[#1C4D88] bg-[#F2F8FC]'
                            : isDarkMode
                              ? 'text-slate-200 hover:text-[#8AC4E6] hover:bg-slate-700/50'
                              : 'text-[#3B4754] hover:text-[#0066A3] hover:bg-[#F2F8FC]'
                            }`}
                          style={{ fontWeight: isActive(item.href) ? 600 : 500 }}
                        >
                          {item.name}
                        </Link>
                      )}

                      {item.hasDropdown && activeDropdown === index && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`ml-4 space-y-1 rounded-md p-1 ${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'
                            }`}
                        >
                          {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                            <Link
                              key={dropdownIndex}
                              to={dropdownItem.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`flex items-center py-2 px-3 rounded-md transition-colors duration-200 text-sm ${isDarkMode
                                ? 'text-slate-300 hover:text-[#8AC4E6] hover:bg-slate-700/50'
                                : 'hover:bg-white/80'
                                }`}
                              style={{ fontWeight: 500, color: isDarkMode ? undefined : '#16202B' }}
                            >
                              <div className={`w-6 h-6 rounded-md flex items-center justify-center mr-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}
                              }`}>
                                <dropdownItem.icon className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                              </div>
                              <div>
                                <div className="axm-nav-item-name" style={{ color: isDarkMode ? undefined : '#16202B' }}>{dropdownItem.name}</div>
                                <div className={`axm-nav-item-desc mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-[#6B7A8B]'}`}>{dropdownItem.description}</div>
                              </div>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </nav>

                <div className={`mt-8 pt-6 border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200/50'
                  }`}>
                  <div className="mb-4">
                    <ThemeToggle variant="menu-item" />
                  </div>
                  {user ? (
                    <div className="space-y-2">
                      {/* User Info */}
                      <div className="text-center py-3 px-4">
                        <div className="flex items-center justify-center mb-2">
                          <div className="relative w-12 h-12">
                            {userAvatar ? (
                              <img
                                src={userAvatar}
                                alt={user.fullName || user.username}
                                className="w-full h-full rounded-full object-cover"
                                onError={(e) => {
                                  // Fallback to initials if image fails
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    const fallback = document.createElement('div');
                                    fallback.className = 'w-full h-full bg-[#0066A3] flex items-center justify-center text-white text-sm font-bold rounded-full';
                                    fallback.textContent = getInitials();
                                    parent.appendChild(fallback);
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-[#0066A3] flex items-center justify-center text-white text-sm font-bold rounded-full">
                                {getInitials()}
                              </div>
                            )}
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
                          </div>
                        </div>
                        <p className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                          {user.fullName || user.username}
                        </p>
                        <p className={`text-sm capitalize ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{user.role}</p>
                      </div>

                      {/* User Actions */}
                      <button
                        onClick={() => {
                          handleDashboardClick();
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center justify-center py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
                          isDarkMode
                            ? 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {/* <User className="w-4 h-4 mr-2" /> */}
                        Dashboard
                      </button>

                      <button
                        onClick={() => {
                          handleProfileClick();
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center justify-center py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
                          isDarkMode
                            ? 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Profile Settings
                      </button>

                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center justify-center py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
                          isDarkMode
                            ? 'bg-red-950/40 hover:bg-red-900/50 text-red-400 border border-red-800/40'
                            : 'bg-red-100 hover:bg-red-200 text-red-700'
                        }`}
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  ) : !(location.pathname === '/login' || location.pathname.startsWith('/login/')) && (
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full bg-[#0084CF] hover:bg-[#0066A3] text-white text-center py-3 px-4 rounded font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      Login
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AxonMetricHeader;
